/**
 * @file chat.js
 * @description Provider-agnostic chat facade.
 *
 * The active chat provider + its credentials come from the API at runtime
 * (the decrypted `chatProvider` object in the language store — the same payload
 * that carries the Intercom App ID). `ZohoChatButtonControl` reads that store
 * value and pushes the resolved provider here via `setActiveChatProvider()` so
 * that non-React callers (e.g. the game-screen fullscreen handlers) can use the
 * facade without store access.
 *
 * One provider per tenant. Provider-specific concerns that have no shared shape
 * — e.g. Intercom's authenticated boot/identity, Zendesk's snippet injection —
 * stay in the consumer, gated on the resolved provider.
 */

import { CHAT_PROVIDERS } from 'utils/constants'
import * as intercom from 'utils/intercom'
import * as zendesk from 'utils/zendesk'

// Runtime-resolved active provider (set once the API chatProvider payload arrives).
let activeProvider = null

/**
 * Set the active chat provider. Called by ZohoChatButtonControl when the API
 * `chatProvider` payload resolves. Value is a CHAT_PROVIDERS string.
 *
 * @param {string|null} provider
 */
export const setActiveChatProvider = (provider) => {
  activeProvider = provider || null
}

/**
 * @returns {string|null} The active CHAT_PROVIDERS value, or null before the API resolves.
 */
export const getActiveChatProvider = () => activeProvider

/**
 * Whether the active provider supports authenticated identity in this integration.
 * Zendesk is anonymous-only here, so identity effects are skipped for it.
 */
export const isChatAuthSupported = () => activeProvider === CHAT_PROVIDERS.INTERCOM

export const hideLauncher = () => {
  if (activeProvider === CHAT_PROVIDERS.ZENDESK) zendesk.hideLauncher()
  else if (activeProvider === CHAT_PROVIDERS.INTERCOM) intercom.hideLauncher()
}

export const showLauncher = () => {
  if (activeProvider === CHAT_PROVIDERS.ZENDESK) zendesk.showLauncher()
  else if (activeProvider === CHAT_PROVIDERS.INTERCOM) intercom.showLauncher()
}

export const openChat = (languageCode) => {
  if (activeProvider === CHAT_PROVIDERS.ZENDESK) zendesk.openChat(languageCode)
  else if (activeProvider === CHAT_PROVIDERS.INTERCOM) intercom.openChat(languageCode)
}

export const setLanguage = (languageCode) => {
  if (activeProvider === CHAT_PROVIDERS.ZENDESK) zendesk.setLocale(languageCode)
  else if (activeProvider === CHAT_PROVIDERS.INTERCOM) intercom.updateLanguage(languageCode)
}
